import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AccountingPlanStatus } from 'enums/AccountingPlanStatus';
import { LedgerPostType } from 'enums/LedgerPostType';
import { MessageType } from 'enums/MessageType';
import { environment } from 'environments/environment';
import { AccountingPlansLedgerPostsAmounts } from 'models/AccountingPlansLedgerPostsAmounts';
import { AccountingPlanSearchRequestModel } from 'models/generated/Accounting.Api.Models.AccountingPlanRequest';
import { BillingPlanDtoModel } from 'models/generated/Accounting.Api.Models.BillingPlanDto';
import { CreditWizardRequestModel, EndAccountingPlanDtoModel } from 'models/generated/Accounting.Api.Models.CreditWizardRequest';
import { LedgerPostSearchModel } from 'models/generated/Accounting.Api.Models.LedgerPostSearch';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { AccountingPlanSetupModel } from 'models/generated/Entities.Accounting.AccountingPlanSetup';
import { CompanyAccountingSettingModel } from 'models/generated/Entities.Accounting.CompanyAccountingSetting';
import { LedgerPostModel } from 'models/generated/Entities.Accounting.LedgerPost';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { FarPayResponseModel } from 'models/generated/GormGateway.Models.FarPayResponse';
import { UserModel } from 'models/generated/Identity.Api.Models.User';
import { SelectListItemModel } from 'models/SelectList';
import { firstValueFrom } from 'rxjs';
import { LiveDataTracker, TrackingContext } from 'services/data-tracking';
import { DateService } from 'services/date.service';
import { PushService } from 'services/push.service';
import { Base64File, FileStorageService } from './file-storage.service';
import { InstallmentPaymentModel } from 'models/generated/Entities.Accounting.InstallmentPayment';
import { LedgerHeaderModel } from 'models/generated/Entities.Accounting.LedgerHeader';

export class AccountingPlanLog {
	user?: UserModel;
	previousValue!: AccountingPlanModel;
	currentValue!: AccountingPlanModel;
	accountingPlanId!: string;
	timestamp!: Date;
}

@Injectable()
export class AccountingService {

	private accountingUrl = `${environment.gatewayUrl}/api/accounting`;
	private gatewayUrl = `${environment.gatewayUrl}/api/gateway`;
	private farPayUrl = `${environment.gatewayUrl}/api/farpay`;
	private accountingPlanTracker = new LiveDataTracker<AccountingPlanModel, { accountingPlanId: string, productPlanId?: string }>(plan => plan.id!);
	private ledgerPostTracker = new LiveDataTracker<LedgerPostModel, { accountingPlanId?: string, productPlanId?: string, companyId?: string }>(post => post.id!);
	constructor(private readonly http: HttpClient,
		private readonly dateService: DateService,
		private readonly pushService: PushService,
		private readonly fileStorageService: FileStorageService
	) {
		this.pushService.onChanges<{ accountingPlanId: string, productPlanId?: string }>(MessageType.AccountingPlanChanges).subscribe(event => {
			const planId = event.data?.accountingPlanId;
			if (planId) this.accountingPlanTracker.refreshData(event, planId, () => this.getAccountingPlan(planId));
		});
	}

	getAllAccountingPlansFromProductPlanIds(productPlanIds: string[], trackingContext?: TrackingContext): Promise<AccountingPlanModel[]> {
		return firstValueFrom(this.http.post<AccountingPlanModel[]>(`${this.accountingUrl}/accounting-plans/get-all-accounting-plans-from-product-plan-ids`, productPlanIds))
			.then(plans => this.accountingPlanTracker.trackArrayForComponent(trackingContext, plans, eventData => productPlanIds.includes(eventData.productPlanId!)));
	}

	getAccountingPlan(accountingPlanId: string): Promise<AccountingPlanModel> {
		return firstValueFrom(this.http.get<AccountingPlanModel>(`${this.accountingUrl}/accounting-plans/${accountingPlanId}`));
	}

	getMaxCounterValue(accountingPlanId: string): Promise<number> {
		return firstValueFrom(this.http.get<number>(`${this.accountingUrl}/accounting-plans/${accountingPlanId}/max-counter-value`));
	}

	getAccountingPlansFromProductPlanId(productPlanId: string, trackingContext?: TrackingContext): Promise<AccountingPlanModel[]> {
		return firstValueFrom(this.http.get<AccountingPlanModel[]>(`${this.accountingUrl}/accounting-plans/from-product-plan/${productPlanId}`))
			.then(plans => this.accountingPlanTracker.trackArrayForComponent(trackingContext, plans));
	}

	getInDebtCollection(companyIds: string[]): Promise<string[]> {
		return firstValueFrom(this.http.post<string[]>(`${this.accountingUrl}/companies-in-debt-collection`, companyIds));
	}

	getCurrentAccountingPlansFromProductPlanIds(productPlanIds: string[], trackingContext?: TrackingContext): Promise<AccountingPlanModel[]> {
		return firstValueFrom(this.http.post<AccountingPlanModel[]>(`${this.accountingUrl}/accounting-plans/get-current-accounting-plans-from-product-plan-ids`, productPlanIds))
			.then(plans => this.accountingPlanTracker.trackArrayForComponent(trackingContext, plans));
	}

	setAccountingPlanPriceAdjustment(accountingPlan: AccountingPlanModel, setIgnorePriceAdjustments: boolean) {
		return firstValueFrom(this.http.patch<void>(`${this.gatewayUrl}/accounting-plans/${accountingPlan.id}`, { ignorePriceAdjustments: setIgnorePriceAdjustments }));
	}
	
	getAccountingPlanSetupsSelectList(onlyActive?: boolean): Promise<SelectListItemModel[]> {
		return this.getAccountingPlanSetups(onlyActive).then(accountingPlanSetups => {
			if (accountingPlanSetups.length === 0) throw new Error('Missing accounting plan setup');
			return accountingPlanSetups.sort((aps1, aps2) => aps1.name.localeCompare(aps2.name)).map(aps => new SelectListItemModel(aps.id!, aps.name));
		});
	}

	generateAnnualContractValue(startDate?: Date, endDate?: Date, totalPlanValue?: number): number | undefined {
		if (!startDate || !endDate || !totalPlanValue) return undefined;
		const planDuration = this.dateService.getDiffInMonths(startDate, this.dateService.addDays(endDate, 1));
		return planDuration > 0 ? (totalPlanValue / planDuration * 12) : undefined;
	}

	getAccountingPlanSetups(onlyActive?: boolean): Promise<AccountingPlanSetupModel[]> {
		return firstValueFrom(this.http.get<AccountingPlanSetupModel[]>(`${this.accountingUrl}/accounting-plan-setups?onlyActive=${onlyActive}`));
	}

	saveAccountingPlanSetup(accountingPlanSetup: AccountingPlanSetupModel): Promise<AccountingPlanSetupModel> {
		if (accountingPlanSetup.id)
			return firstValueFrom(this.http.put<AccountingPlanSetupModel>(`${this.accountingUrl}/accounting-plan-setups`, accountingPlanSetup));
		return firstValueFrom(this.http.post<AccountingPlanSetupModel>(`${this.accountingUrl}/accounting-plan-setups`, accountingPlanSetup));
	}

	deleteAccountingPlanSetup(accountingPlanSetupId: string): Promise<void> {
		return firstValueFrom(this.http.delete<void>(`${this.accountingUrl}/accounting-plan-setups/${accountingPlanSetupId}`));
	}

	deleteAccountingPlan(accountingPlanId: string) {
		return firstValueFrom(this.http.delete(`${this.gatewayUrl}/accounting-plans/${accountingPlanId}`));
	}

	registerStartOfPlanByProductPlanId(productPlanId: string) {
		return firstValueFrom(this.http.post<{ productPlanId: string, warningCode: string; }[]>(`${this.gatewayUrl}/accounting-plans/register-start-of-plan/product-plan/${productPlanId}`, null));
	}

	registerStartOfPlan(accountingPlanId: string) {
		return firstValueFrom(this.http.post<{ productPlanId: string, warningCode: string; }[]>(`${this.gatewayUrl}/accounting-plans/register-start-of-plan/${accountingPlanId}`, null));
	}

	updateBillingPlanAndTotalValue(billingPlan: BillingPlanDtoModel) {
		billingPlan.include(bp => bp.ledgerPosts);
		return firstValueFrom(this.http.put<void>(`${this.accountingUrl}/accounting-plans/billingplan-and-totalplanvalue`, billingPlan));
	}

	unsuspend(productPlanId: string, billingPlan?: BillingPlanDtoModel) {
		billingPlan?.include(bp => bp.ledgerPosts);
		return firstValueFrom(this.http.put<void>(`${this.accountingUrl}/accounting-plans/unsuspend/${productPlanId}`, billingPlan));
	}

	updateMigratedLedgerPostAndTotalValue(billingPlan: BillingPlanDtoModel) {
		billingPlan.include(bp => bp.ledgerPosts);
		return firstValueFrom(this.http.put<void>(`${this.accountingUrl}/accounting-plans/migrated-post-and-total-plan-value`, billingPlan));
	}

	getCompanySettings(companyId: string) {
		return firstValueFrom(this.http.get<CompanyAccountingSettingModel[]>(`${this.accountingUrl}/company-accounting-settings/${companyId}`));
	}
	findCompanySettingsByCustomerNo(customerNo: string) {
		return firstValueFrom(this.http.get<CompanyAccountingSettingModel[]>(`${this.accountingUrl}/company-accounting-settings/find-by-customer-no?customerNo=${customerNo}`));
	}
	updateNoHandlingFees(companyId: string, noHandlingFees: boolean) {
		return firstValueFrom(this.http.patch(`${this.accountingUrl}/company-accounting-settings/${companyId}/no-handling-fees?noHandlingFees=${noHandlingFees}`, null));
	}

	filterActiveAccountingPlan(accountingPlans: AccountingPlanModel[]): AccountingPlanModel {
		return accountingPlans.filter(ap => !ap.$deleted).sort((a, b) => {
			if (!a.endDate)
				return -1;
			if (!b.endDate)
				return 1;
			return b.endDate!.getTime() - a.endDate!.getTime();
		})[0];
	}

	filterCurrentAccountingPlan(accountingPlans: AccountingPlanModel[]): AccountingPlanModel {
		return accountingPlans.find(ap => !ap.$deleted && ap.startDate && ap.startDate <= new Date() && ap.endDate && ap.endDate >= new Date()) ?? this.filterActiveAccountingPlan(accountingPlans);
	}

	hasActiveAccountingPlan(accountingPlans: AccountingPlanModel[]): boolean {
		return this.isActive(this.filterCurrentAccountingPlan(accountingPlans));
	}

	endAccountingPlan(endPlanRequest: EndAccountingPlanDtoModel): Promise<void> {
		return firstValueFrom(this.http.put<void>(`${this.gatewayUrl}/accounting-plans/end-plan`, endPlanRequest));
	}

	undoTerminateAccountingPlan(accountingPlanId: string, endDate?: Date): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.gatewayUrl}/accounting-plans/undo-terminate`, {accountingPlanId:accountingPlanId, endDate:endDate}));
	}

	createCreditMemo(creditRequest: CreditWizardRequestModel): Promise<void> {
		creditRequest.include(cr => cr.credits);
		creditRequest.include(cr => cr.endAccountingPlans);
		creditRequest.include(cr => cr.billingPlans);
		creditRequest.billingPlans.forEach(entry => {
			entry.include(cr => cr.ledgerPosts);
		})
		return firstValueFrom(this.http.post<void>(`${this.accountingUrl}/credit`, creditRequest));
	}

	getAccountingPlansByStatus(status: AccountingPlanStatus): Promise<AccountingPlanModel[]> {
		return firstValueFrom(this.http.get<AccountingPlanModel[]>(`${this.accountingUrl}/accounting-plans/by-status/${status}`));
	}
	setPlanStatus(fromStatus: AccountingPlanStatus, toStatus: AccountingPlanStatus, accountingPlanId: string): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.accountingUrl}/accounting-plans/${accountingPlanId}/set-status/${fromStatus}/${toStatus}`, null));
	}

	statusColor(productPlan: ProductPlanModel, accountingPlan?: AccountingPlanModel): string {
		return ({
			'delivered'	: 'color-gray-80',
			'suspended'	: 'color-red-50',
			'expired'	: 'color-grey-40',
			'terminated': 'color-yellow-50',
			'future'	: 'color-blue-50',
		})[this.getStatus(productPlan, accountingPlan)] ?? 'color-green-50';
	}

	statusTitle(productPlan: ProductPlanModel, accountingPlan?: AccountingPlanModel): string {
		return ({
			'delivered': 'company.product-plans.status.delivered',
			'suspended': 'company.product-plans.status.suspended',
			'expired'	: 'company.product-plans.status.expired',
			'terminated': 'company.product-plans.status.terminated',
			'future'	: 'company.product-plans.status.future',
		})[this.getStatus(productPlan, accountingPlan)] ?? 'company.product-plans.status.active';
	}

	getStatus(productPlan: ProductPlanModel, accountingPlan?: AccountingPlanModel): string {
		if (!accountingPlan) return 'future';
		if (accountingPlan.suspended) return 'suspended';
		if ((!accountingPlan.startDate || accountingPlan.startDate >= new Date(Date.now()))) return 'future';
		if (!productPlan.autoRenewable) return 'delivered';
		if (accountingPlan.endDate && accountingPlan.endDate < new Date(Date.now())) return 'expired';
		if (productPlan.terminationDate) return 'terminated';
		return 'active';
	}

	isActive(accountingPlan?: AccountingPlanModel): boolean {
		return (!accountingPlan?.endDate || new Date(accountingPlan.endDate) >= this.dateService.today());
	}

	searchLedgerPosts(request: LedgerPostSearchModel): Promise<LedgerPostModel[]> {
		return firstValueFrom(this.http.post<LedgerPostModel[]>(`${this.accountingUrl}/ledger-posts/search`, request));
	}

	searchAccountingPlans(request: AccountingPlanSearchRequestModel): Promise<AccountingPlanModel[]> {
		return firstValueFrom(this.http.post<AccountingPlanModel[]>(`${this.accountingUrl}/accounting-plans/search`, request));
	}

	getLedgerPostsByProductPlanIds(productPlanIds: string[], historicalDate?: Date): Promise<LedgerPostModel[]> {
		return firstValueFrom(this.http.post<LedgerPostModel[]>(`${this.accountingUrl}/ledger-posts/by-product-plans${(historicalDate ? `?historicalPointInTime=${historicalDate}` : '')}`, productPlanIds));
	}

	async getLedgerPostsByPostedDocumentNo(documentNumber: string, brandId: string): Promise<LedgerPostModel[]> {
		const ledgerHeaders = await this.getLedgerHeadersByPostedDocumentNumbers([documentNumber], brandId);
		return ledgerHeaders.flatMap(header => header.ledgerPosts ?? []);
	}

	getLedgerHeadersByPostedDocumentNumbers(documentNumbers: string[], brandId: string): Promise<LedgerHeaderModel[]> {
		return firstValueFrom(this.http.post<LedgerHeaderModel[]>(`${this.accountingUrl}/ledger-posts/by-posted-document-numbers`, documentNumbers, { params: { brandId } }));
	}

	getHistoricalChangeDates(accountingPlanId: string): Promise<Date[]> {
		return firstValueFrom(this.http.get<Date[]>(`${this.accountingUrl}/ledger-posts/historical-change-dates/${accountingPlanId}`));
	}
	generateSuggestedBillingPlan(accountingPlanId: string, paymentInterval: number, maxNumberOfPayments?: number): Promise<LedgerPostModel[]> {
		return firstValueFrom(this.http.get<LedgerPostModel[]>(`${this.accountingUrl}/accounting-plans/${accountingPlanId}/suggested-billingplan-by-interval?paymentInterval=${paymentInterval}&maxNumberOfPayments=${maxNumberOfPayments}`));
	}
	updateBillingPlanAndInterval(accountingPlanId: string, paymentInterval: number, numberOfInstallments: number|undefined, ledgerPosts: LedgerPostModel[], expectedTotalValue: number): Promise<void> {
		const request = new BillingPlanDtoModel();
		request.accountingPlanId = accountingPlanId;
		request.expectedTotalValue = expectedTotalValue;
		request.ledgerPosts = ledgerPosts;
		request.include(r => r.ledgerPosts);

		let query = `paymentInterval=${paymentInterval}`;
		if (numberOfInstallments)
			query += `&numberOfInstallments=${numberOfInstallments!}`;

		return firstValueFrom(this.http.put<void>(`${this.accountingUrl}/accounting-plans/billingplan-and-interval?${query}`, request))
	}

	changeAccountingPlanEndDate(accountingPlanId: string, endDate: Date, expectedTotalPlanValue: number): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.accountingUrl}/accounting-plans/${accountingPlanId}/end-date`, { endDate, expectedTotalPlanValue }));
	}

	static addVat(amount: number) {
		return Math.round(125 * amount + Number.EPSILON) / 100;
	}

	static subtractVat(amount: number) {
		return Math.round(80 * amount + Number.EPSILON) / 100;
	}

	async getPlansOpenAndClosedAmounts(productPlanIds: string[]): Promise<AccountingPlansLedgerPostsAmounts>{
		let plansAmount = new AccountingPlansLedgerPostsAmounts();
		let ledgerPosts = await this.getLedgerPostsByProductPlanIds(productPlanIds);

		ledgerPosts.forEach(post => {
			if (post.type == LedgerPostType.MigratedInvoice || post.type == LedgerPostType.CounterPost || post.type == LedgerPostType.EqualizingCounterPost || post.ledgerHeader?.closed)
				plansAmount.add(post.accountingPlan!.productPlanId!, post.accountingPlanId!, 0, post.signedValue);
			else
				plansAmount.add(post.accountingPlan!.productPlanId!, post.accountingPlanId!, post.signedValue, 0);
		});

		return plansAmount;
	}

	async getFarPayInfo(customerNumbers: { customerNo?: string, brandId?: string; }[]): Promise<FarPayResponseModel> {
		if (customerNumbers.filter(cn => cn.customerNo).length == 0) return new FarPayResponseModel();
		return await firstValueFrom(this.http.post<FarPayResponseModel>(`${this.farPayUrl}/customers/find`, customerNumbers));
	}

	async getFarPayInvoiceIdsByCompanyId(companyId: string, brandId: string): Promise<Record<string, number>> {
		return await firstValueFrom(this.http.get<Record<string, number>>(`${this.farPayUrl}/invoices/${companyId}`, { params: { brandId }}));
	}

	async downloadFarPayInvoice(invoiceId: number, filename: string, brandId: string) {
		this.http.get<Base64File>(`${this.farPayUrl}/download-invoice/${invoiceId}`, { params: { brandId } }).subscribe(response => {
			this.fileStorageService.downloadFileByBase64(response, filename || 'faktura.pdf');
		});
	}

	async sendFarPayInvoice(invoiceId: number, brandId: string) {
		await firstValueFrom(this.http.get(`${this.farPayUrl}/send-invoice/${invoiceId}`, { params: { brandId } }));
	}

	async getInstallmentPayments(documents: { documentNo?: string, brandId?: string }[]) {
		return await firstValueFrom(this.http.post<InstallmentPaymentModel[]>(`${this.accountingUrl}/get-installment-payments`, documents));
	}

	async setRemainingInstallmentPayments(brandId: string, documentNo: string, payments: InstallmentPaymentModel[]) {
		return await firstValueFrom(this.http.post(`${this.accountingUrl}/installment-payments/${brandId}/${documentNo}`, payments));
	}

}
